/**
 * Dispute respond — the respondent's view. Shows the claim above the
 * form (so context isn't lost) + a deadline countdown banner that
 * grades by remaining time, then the response form (description +
 * EvidencePicker).
 *
 * Submit calls `useRespondToDispute` which transitions the dispute to
 * `under_review` server-side. The `responded_at IS NULL` guard in the
 * service prevents a double-respond race.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import { Calendar, AlertCircle } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Skeleton } from '@/components/ui/Skeleton'
import { DisputeStatusPill } from '@/components/disputes/DisputeStatusPill'
import { EvidencePicker, type EvidenceItem } from '@/components/disputes/EvidencePicker'
import { useDispute, useRespondToDispute } from '@/lib/hooks/use-disputes'
import { useAuth } from '@/lib/hooks/use-auth'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'

const MAX_PHOTOS = 10

type CountdownTone = 'success' | 'amber' | 'coral' | 'expired'

function countdownState(deadline: string | null): { tone: CountdownTone; hours: number } {
  if (!deadline) return { tone: 'success', hours: 0 }
  const ms = new Date(deadline).getTime() - Date.now()
  if (ms <= 0) return { tone: 'expired', hours: 0 }
  const hours = Math.floor(ms / (60 * 60 * 1000))
  if (hours >= 24) return { tone: 'success', hours }
  if (hours >= 6) return { tone: 'amber', hours }
  return { tone: 'coral', hours }
}

export default function DisputeRespondScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const params = useLocalSearchParams<{ disputeId: string }>()
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const { data: dispute, isLoading } = useDispute(params.disputeId)
  const { mutateAsync: respond, isPending } = useRespondToDispute()

  const [response, setResponse] = useState('')
  const [evidence, setEvidence] = useState<EvidenceItem[]>([])

  // Recompute the countdown each render — no need for a setInterval; the
  // user's interaction (typing, scrolling) re-renders frequently enough
  // that the displayed hours stay accurate within ~1 minute.
  const cd = useMemo(
    () => countdownState(dispute?.responseDeadline ?? null),
    [dispute?.responseDeadline],
  )

  // Bounce viewers who shouldn't be on this screen back to the detail
  // view. Defensive — the detail screen's CTA also gates on this, but
  // a deep link could land here directly.
  useEffect(() => {
    if (!dispute) return
    const isRespondent = dispute.respondentId === userId
    const isOpen = dispute.status === 'awaiting_response' || dispute.status === 'open'
    const alreadyResponded = !!dispute.respondedAt
    if (!isRespondent || !isOpen || alreadyResponded) {
      router.replace(`/booking/dispute/${dispute.id}` as never)
    }
  }, [dispute, userId, router])

  const canSubmit = !!dispute && !isPending && cd.tone !== 'expired' && response.trim().length >= 10

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !dispute) return
    impact(ImpactStyle.Medium)
    try {
      await respond({
        disputeId: dispute.id,
        responseDescription: response.trim(),
        evidence,
      })
      void notification(NotificationType.Success)
      toast.success(t('disputes.responded_success'))
      router.replace(`/booking/dispute/${dispute.id}` as never)
    } catch (err) {
      // Surface the real reason and capture it. Dispute-respond is a
      // trust-critical flow with a 72h response-deadline timer — silent
      // failures here can cause a respondent to miss the window and
      // auto-escalate to admin review with no signal of why.
      if (__DEV__) console.error('[Respond dispute]', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('disputes.respond_failed')))
      captureError(err, {
        screen: 'booking/dispute/respond',
        action: 'respond-dispute',
        disputeId: dispute?.id ?? '(missing)',
      })
    }
  }, [canSubmit, dispute, response, evidence, respond, t, router])

  if (isLoading || !dispute) {
    return (
      <ScreenContainer scroll={false}>
        <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md }}>
          <ScreenHeader style={{ paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0 }} />
          <View style={{ marginTop: SPACING.lg, gap: SPACING.md }}>
            <Skeleton width="100%" height={120} borderRadius={RADIUS.lg} />
            <Skeleton width="100%" height={200} borderRadius={RADIUS.lg} />
          </View>
        </View>
      </ScreenContainer>
    )
  }

  const tone = cd.tone

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('disputes.respond_title')} />

      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING.xl * 2,
          gap: SPACING.lg,
        }}
        keyboardShouldPersistTaps="handled"
      >
        {/* Deadline banner */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.sm,
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            backgroundColor:
              tone === 'expired' || tone === 'coral'
                ? `${COLORS.coral.DEFAULT}14`
                : tone === 'amber'
                  ? 'rgba(249, 168, 38, 0.14)'
                  : 'rgba(34, 139, 87, 0.14)',
          }}
        >
          <Calendar
            size={18}
            color={
              tone === 'expired' || tone === 'coral'
                ? colors.textAccent
                : tone === 'amber'
                  ? isDark
                    ? COLORS.amber.DEFAULT
                    : COLORS.amber.dark
                  : colors.success
            }
          />
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color:
                tone === 'expired' || tone === 'coral'
                  ? colors.textAccent
                  : tone === 'amber'
                    ? isDark
                      ? COLORS.amber.DEFAULT
                      : COLORS.amber.dark
                    : colors.success,
            }}
          >
            {tone === 'expired'
              ? t('disputes.deadline_expired')
              : `${t('disputes.deadline_remaining')} ${cd.hours}h`}
          </Text>
        </View>

        {/* Claim summary (read-only context) */}
        <View
          style={{
            gap: SPACING.sm,
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            backgroundColor: colors.surface,
            ...WEB_SHADOWS.sm,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
            <DisputeStatusPill status={dispute.status} />
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {t(`disputes.category_${dispute.category}`)}
            </Text>
          </View>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.bodyBold,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            {t('disputes.respond_claim_summary')}
          </Text>
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
              lineHeight: 20,
            }}
          >
            {dispute.claimDescription}
          </Text>
        </View>

        {/* Form: description */}
        <TextInput
          label={t('disputes.response_label')}
          value={response}
          onChangeText={setResponse}
          placeholder={t('disputes.response_placeholder')}
          multiline
          numberOfLines={4}
          testID="disputes.response-description"
        />

        {/* Form: evidence */}
        <View style={{ gap: SPACING.sm }}>
          <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('disputes.response_evidence_label')}
          </Text>
          <EvidencePicker
            items={evidence}
            onChange={setEvidence}
            max={MAX_PHOTOS}
            testID="disputes.respond-evidence-picker"
          />
        </View>

        {tone === 'expired' && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: `${COLORS.coral.DEFAULT}10`,
            }}
          >
            <AlertCircle size={16} color={COLORS.coral.DEFAULT} />
            <Text
              style={{
                flex: 1,
                fontSize: 13,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                lineHeight: 18,
              }}
            >
              {t('disputes.deadline_expired_explainer')}
            </Text>
          </View>
        )}

        <Button
          title={t('disputes.respond_submit')}
          onPress={handleSubmit}
          variant="primary"
          size="lg"
          disabled={!canSubmit}
          loading={isPending}
          testID="disputes.respond-submit"
        />
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
